import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { MapPin, Target, Zap, CheckCircle2, X, Send, DollarSign, Users, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GeoFencingUpsell({ client, getHeaders, variant = 'full' }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    monthly_budget: '',
    target_radius: '1',
    goals: '',
    notes: '',
  });

  const hdrs = () => ({ ...getHeaders(), 'Content-Type': 'application/json' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/advertising/inquiry`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ service: 'geo_fencing', ...form }),
      });
      if (res.ok) setSubmitted(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <Card className="bg-gradient-to-r from-green-500 to-emerald-600 border-0" data-testid="geofencing-submitted">
        <CardContent className="p-5 text-center">
          <CheckCircle2 className="w-8 h-8 text-white mx-auto mb-2" />
          <h3 className="text-sm font-bold text-white mb-1">Request Submitted!</h3>
          <p className="text-xs text-green-100">Your account manager will reach out within 24 hours to discuss your geo-fencing campaign.</p>
        </CardContent>
      </Card>
    );
  }

  // Compact variant for Overview page
  if (variant === 'compact') {
    return (
      <Card className="bg-gradient-to-r from-violet-600 to-purple-700 border-0 overflow-hidden relative cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowForm(true)} data-testid="geofencing-upsell-compact">
        <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-white">Geo-Fencing Ads</h3>
            <p className="text-xs text-purple-200">Target customers within miles of your business with location-based ads</p>
          </div>
          <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white text-xs border border-white/30 flex-shrink-0" data-testid="geofencing-learn-more">
            Learn More
          </Button>
        </CardContent>
        {showForm && <InquiryModal form={form} setForm={setForm} onSubmit={handleSubmit} onClose={() => setShowForm(false)} submitting={submitting} />}
      </Card>
    );
  }

  // Full variant for Business Links & Advertising tab
  return (
    <>
      <Card className="bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-800 border-0 overflow-hidden relative" data-testid="geofencing-upsell-full">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <CardContent className="p-6 relative">
          <Badge className="bg-white/20 text-white text-[10px] mb-3">New Service</Badge>
          <h3 className="text-lg font-black text-white mb-2 flex items-center gap-2">
            <Target className="w-5 h-5" /> Geo-Fencing Advertising
          </h3>
          <p className="text-sm text-purple-100 mb-4 max-w-xl">
            Target every customer who walks within a set radius of your business — or your competitors. 
            Show them ads on Facebook, Instagram, and Google the moment they're nearby.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
              <MapPin className="w-4 h-4 text-purple-200 mb-1.5" />
              <p className="text-xs font-semibold text-white">Location Targeting</p>
              <p className="text-[11px] text-purple-200 mt-0.5">Reach people within 1-10 miles of any address</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
              <Users className="w-4 h-4 text-purple-200 mb-1.5" />
              <p className="text-xs font-semibold text-white">Competitor Conquest</p>
              <p className="text-[11px] text-purple-200 mt-0.5">Target people visiting your competitors' locations</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
              <TrendingUp className="w-4 h-4 text-purple-200 mb-1.5" />
              <p className="text-xs font-semibold text-white">Measurable ROI</p>
              <p className="text-[11px] text-purple-200 mt-0.5">Track walk-ins, calls, and orders from every ad</p>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-4 mb-5 backdrop-blur-sm">
            <p className="text-xs text-purple-200 font-medium mb-2">Pricing</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-lg font-black text-white">$299</p>
                <p className="text-[10px] text-purple-300">One-time setup</p>
              </div>
              <div>
                <p className="text-lg font-black text-white">$149</p>
                <p className="text-[10px] text-purple-300">/month management</p>
              </div>
              <div>
                <p className="text-lg font-black text-white">15%</p>
                <p className="text-[10px] text-purple-300">of ad spend</p>
              </div>
              <div>
                <p className="text-lg font-black text-white">You set</p>
                <p className="text-[10px] text-purple-300">your ad budget</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={() => setShowForm(true)} className="bg-white text-purple-700 hover:bg-purple-50 text-xs font-semibold px-6" data-testid="geofencing-get-started">
              <Zap className="w-3 h-3 mr-1.5" /> Get Started
            </Button>
            <span className="text-xs text-purple-300">No commitment — let's discuss your goals first</span>
          </div>
        </CardContent>
      </Card>

      {showForm && <InquiryModal form={form} setForm={setForm} onSubmit={handleSubmit} onClose={() => setShowForm(false)} submitting={submitting} />}
    </>
  );
}

function InquiryModal({ form, setForm, onSubmit, onClose, submitting }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="geofencing-inquiry-modal">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-600" /> Geo-Fencing Inquiry
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Tell us about your advertising goals</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Monthly Ad Budget</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input value={form.monthly_budget} onChange={e => setForm(f => ({ ...f, monthly_budget: e.target.value }))}
                placeholder="e.g., 500" className="pl-9 bg-gray-50 border-gray-200 text-gray-800" data-testid="geo-budget" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">This is what you'd spend on the actual ads (separate from our management fee)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Target Radius (miles)</label>
            <select value={form.target_radius} onChange={e => setForm(f => ({ ...f, target_radius: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50" data-testid="geo-radius">
              <option value="1">1 mile</option>
              <option value="3">3 miles</option>
              <option value="5">5 miles</option>
              <option value="10">10 miles</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">What are your goals?</label>
            <select value={form.goals} onChange={e => setForm(f => ({ ...f, goals: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50" data-testid="geo-goals">
              <option value="">Select...</option>
              <option value="more_foot_traffic">More foot traffic / walk-ins</option>
              <option value="more_calls">More phone calls</option>
              <option value="more_orders">More online orders</option>
              <option value="brand_awareness">Brand awareness in my area</option>
              <option value="competitor_conquest">Target competitor customers</option>
              <option value="all">All of the above</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Anything else we should know?</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Competitor locations to target, seasonal promotions, etc."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50 resize-none"
              rows={3} data-testid="geo-notes" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm" data-testid="geo-submit">
            {submitting ? 'Submitting...' : <><Send className="w-3.5 h-3.5 mr-1.5" /> Submit Inquiry</>}
          </Button>
          <p className="text-[10px] text-gray-400 text-center">Your account manager will follow up within 24 hours</p>
        </form>
      </div>
    </div>
  );
}
